import {
  mcGetCloudItem,
  mcGetItem,
  mcSetCachedItem,
  mcSetCloudItemIfRevision,
  mcSubscribeCloudItem,
  mcRemoveItem,
} from "./mcCloudSync";
import { DEFAULT_UNIT_LIBRARY } from "./mcEngineData";

/**
 * Shared Unit Intelligence Service
 *
 * INF-SC-004 Phase A foundation. This module intentionally has no UI
 * dependency. It owns the site-scoped Unit Intelligence document and exposes
 * one API for cache-first peeking, cloud-first hydration, guarded persistence,
 * subscription and same-browser broadcast.
 */

export const MC_SHARED_UNIT_INTELLIGENCE_EVENT = "mc-shared-unit-intelligence-change";
export const MC_SHARED_UNIT_INTELLIGENCE_KEY = "mc-shared-unit-intelligence";

const cleanText = (value) => String(value ?? "").trim();
const positiveNumber = (value, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

function normalizeScope(scope = "ALL") {
  const raw = cleanText(scope);
  if (!raw || raw.toUpperCase() === "ALL") return "ALL";
  return raw
    .replace(/^inventory_/i, "")
    .replace(/_Store$/i, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "ALL";
}

export function sharedUnitIntelligenceStorageKey(scope = "ALL") {
  const normalizedScope = normalizeScope(scope);
  return normalizedScope === "ALL"
    ? MC_SHARED_UNIT_INTELLIGENCE_KEY
    : `${MC_SHARED_UNIT_INTELLIGENCE_KEY}:${normalizedScope}`;
}

function normalizeUnitLibrary(library = []) {
  const source = Array.isArray(library) && library.length ? library : DEFAULT_UNIT_LIBRARY;
  const seen = new Set();
  return source
    .map(cleanText)
    .filter((unit) => {
      const key = unit.toLowerCase();
      if (!unit || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function normalizeUnitRule(rule = {}, fallback = {}) {
  const merged = { ...(fallback || {}), ...(rule || {}) };
  return {
    countUnit: cleanText(merged.countUnit || "Unit") || "Unit",
    baseUnit: cleanText(merged.baseUnit || "Unit") || "Unit",
    systemUnitSize: positiveNumber(merged.systemUnitSize, 1),
    priceUnitSize: positiveNumber(merged.priceUnitSize, 1),
    priceBasis: merged.priceBasis === "base" ? "base" : "count",
    autoDetectSize: Boolean(merged.autoDetectSize),
  };
}

export function normalizeSharedUnitIntelligence(config = {}) {
  const unitLibrary = normalizeUnitLibrary(config?.unitLibrary);
  const defaultUnitRule = normalizeUnitRule(config?.defaultUnitRule);
  const categoryUnitRules = {};

  const sourceRules = config?.categoryUnitRules && typeof config.categoryUnitRules === "object" && !Array.isArray(config.categoryUnitRules)
    ? config.categoryUnitRules
    : {};

  Object.entries(sourceRules).forEach(([category, rule]) => {
    const key = cleanText(category).toLowerCase();
    if (!key || !rule || typeof rule !== "object" || Array.isArray(rule)) return;
    categoryUnitRules[key] = normalizeUnitRule(rule, defaultUnitRule);
  });

  // Ensure every referenced unit is available in the library. This makes a
  // shared record self-contained and prevents a rule from rendering an invalid
  // select value after another browser adds or renames a unit.
  const referencedUnits = [
    defaultUnitRule.countUnit,
    defaultUnitRule.baseUnit,
    ...Object.values(categoryUnitRules).flatMap((rule) => [rule.countUnit, rule.baseUnit]),
  ];
  const completeLibrary = normalizeUnitLibrary([...unitLibrary, ...referencedUnits]);

  return {
    unitLibrary: completeLibrary,
    defaultUnitRule,
    categoryUnitRules,
  };
}

export function parseSharedUnitIntelligenceRecord(raw) {
  if (!raw) return { config: null, payload: null, revision: 0 };
  try {
    const payload = typeof raw === "string" ? JSON.parse(raw) : raw;
    const source = payload?.config && typeof payload.config === "object" ? payload.config : payload;
    return {
      config: normalizeSharedUnitIntelligence(source || {}),
      payload: payload && typeof payload === "object" && !Array.isArray(payload) ? payload : null,
      revision: Number(payload?.revision || 0),
    };
  } catch (_) {
    return { config: null, payload: null, revision: 0 };
  }
}

function buildPayload(config = {}, scope = "ALL", metadata = {}) {
  return {
    version: 1,
    scope: normalizeScope(scope),
    config: normalizeSharedUnitIntelligence(config),
    updatedAt: new Date().toISOString(),
    updatedBy: cleanText(metadata.updatedBy || metadata.user || metadata.email),
    source: cleanText(metadata.source || "inventory-configuration"),
    writeToken: cleanText(metadata.writeToken),
    clientId: cleanText(metadata.clientId),
    revision: Number(metadata.revision || 0),
  };
}

function dispatchUnitIntelligenceEvent(payload, origin = "local") {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MC_SHARED_UNIT_INTELLIGENCE_EVENT, {
    detail: { ...payload, origin },
  }));
}

export function peekSharedUnitIntelligence(scope = "ALL") {
  const key = sharedUnitIntelligenceStorageKey(scope);
  return parseSharedUnitIntelligenceRecord(mcGetItem(key)).config;
}

export async function loadSharedUnitIntelligenceRecord(scope = "ALL", options = {}) {
  const key = sharedUnitIntelligenceStorageKey(scope);
  if (options.preferCloud !== false) {
    const cloudRaw = await mcGetCloudItem(key);
    if (cloudRaw !== null) {
      const serialized = typeof cloudRaw === "string" ? cloudRaw : JSON.stringify(cloudRaw);
      mcSetCachedItem(key, serialized);
      return parseSharedUnitIntelligenceRecord(cloudRaw);
    }
  }
  return parseSharedUnitIntelligenceRecord(mcGetItem(key));
}

export async function loadSharedUnitIntelligence(scope = "ALL", options = {}) {
  return (await loadSharedUnitIntelligenceRecord(scope, options)).config;
}

export async function saveSharedUnitIntelligence(config = {}, scope = "ALL", metadata = {}) {
  const key = sharedUnitIntelligenceStorageKey(scope);
  const payload = buildPayload(config, scope, metadata);
  const serialized = JSON.stringify(payload);

  // Cache and broadcast immediately for a responsive initiating browser. The
  // cache-only API is deliberate: hydration and optimistic updates must never
  // create a write-through loop back into Firestore.
  mcSetCachedItem(key, serialized);
  dispatchUnitIntelligenceEvent(payload, "local");

  const guarded = await mcSetCloudItemIfRevision(key, serialized, Number(metadata.baseRevision || 0));
  if (!guarded.ok) {
    if (guarded.currentValue) mcSetCachedItem(key, guarded.currentValue);
    const current = parseSharedUnitIntelligenceRecord(guarded.currentValue);
    return {
      ok: false,
      verified: false,
      conflict: Boolean(guarded.conflict),
      currentConfig: current.config,
      currentPayload: current.payload,
      revision: Number(guarded.revision || current.revision || 0),
      key,
      payload,
    };
  }

  payload.revision = Number(guarded.revision || 0);
  if (guarded.currentValue) mcSetCachedItem(key, guarded.currentValue);

  // Global publishing only needs the authoritative cloud write acknowledgement.
  // Skip the slower read-back loop so a successful write is not reported as a
  // timeout while Firestore listeners are still converging.
  if (metadata.skipVerification === true) {
    return {
      ok: true,
      cloudSaved: true,
      verified: false,
      verificationSkipped: true,
      conflict: false,
      revision: Number(payload.revision || 0),
      key,
      payload,
    };
  }

  let verified = false;
  for (let attempt = 0; attempt < 6 && !verified; attempt += 1) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
    const cloudRaw = await mcGetCloudItem(key);
    if (cloudRaw === null) continue;
    const cloud = parseSharedUnitIntelligenceRecord(cloudRaw);
    const cloudToken = cleanText(cloud.payload?.writeToken);
    verified = cloudToken === cleanText(payload.writeToken)
      && JSON.stringify(cloud.config) === JSON.stringify(payload.config);
  }

  return {
    ok: verified,
    cloudSaved: true,
    verified,
    conflict: false,
    revision: Number(payload.revision || 0),
    key,
    payload,
  };
}

export function removeSharedUnitIntelligenceOverride(scope = "ALL") {
  const normalizedScope = normalizeScope(scope);
  if (normalizedScope === "ALL") return false;
  mcRemoveItem(sharedUnitIntelligenceStorageKey(normalizedScope));
  return true;
}

export function broadcastSharedUnitIntelligence(config = {}, scope = "ALL", metadata = {}) {
  const payload = buildPayload(config, scope, metadata);
  dispatchUnitIntelligenceEvent(payload, "broadcast");
  return payload;
}

export function subscribeSharedUnitIntelligence(scope = "ALL", callback) {
  if (typeof callback !== "function") return () => {};

  const key = sharedUnitIntelligenceStorageKey(scope);
  const normalizedScope = normalizeScope(scope);
  let lastSignature = "";

  const emit = (config, meta = {}) => {
    if (!config) return;
    const normalizedConfig = normalizeSharedUnitIntelligence(config);
    const writeToken = cleanText(meta?.payload?.writeToken);
    const revision = Number(meta?.payload?.revision || 0);
    const signature = JSON.stringify({ config: normalizedConfig, writeToken, revision });
    if (signature === lastSignature && !meta.force) return;
    lastSignature = signature;
    callback(normalizedConfig, { scope: normalizedScope, key, ...meta });
  };

  const onLocalEvent = (event) => {
    const detail = event?.detail || {};
    if (normalizeScope(detail.scope) !== normalizedScope) return;
    emit(detail.config, { origin: detail.origin || "local-event", payload: detail });
  };

  const onStorage = (event) => {
    if (event?.key !== key) return;
    const parsed = parseSharedUnitIntelligenceRecord(event.newValue);
    emit(parsed.config, { origin: "storage", payload: parsed.payload });
  };

  if (typeof window !== "undefined") {
    window.addEventListener(MC_SHARED_UNIT_INTELLIGENCE_EVENT, onLocalEvent);
    window.addEventListener("storage", onStorage);
  }

  const unsubscribeCloud = mcSubscribeCloudItem(key, (raw, cloudMeta = {}) => {
    if (raw === null) return;
    const parsed = parseSharedUnitIntelligenceRecord(raw);
    const serialized = typeof raw === "string" ? raw : JSON.stringify(raw);
    mcSetCachedItem(key, serialized);
    emit(parsed.config, { origin: "cloud", cloudMeta, payload: parsed.payload });
  });

  const cached = parseSharedUnitIntelligenceRecord(mcGetItem(key));
  if (cached.config) emit(cached.config, { origin: "cache", payload: cached.payload, force: true });

  return () => {
    if (typeof window !== "undefined") {
      window.removeEventListener(MC_SHARED_UNIT_INTELLIGENCE_EVENT, onLocalEvent);
      window.removeEventListener("storage", onStorage);
    }
    if (typeof unsubscribeCloud === "function") unsubscribeCloud();
  };
}

export default {
  keyForScope: sharedUnitIntelligenceStorageKey,
  normalize: normalizeSharedUnitIntelligence,
  peek: peekSharedUnitIntelligence,
  load: loadSharedUnitIntelligence,
  loadRecord: loadSharedUnitIntelligenceRecord,
  save: saveSharedUnitIntelligence,
  subscribe: subscribeSharedUnitIntelligence,
  broadcast: broadcastSharedUnitIntelligence,
};
